import React, { useEffect, useState } from "react";
import type { ReactNode } from "react";
import { useForm } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";

import moment from "moment";

import { completeRegistration } from "@/api/user";

import { checkIfScrollable } from "@/utils/check-if-scrollable";

import { RadioControllerInput } from "../../components/radio-input-controller/radio-with-input";
import Select from "../../components/select/select";
import SurveySlider from "../../components/slider";
import TextInput from "../../components/text-input/text-input";
import type { MVPFormInputs } from "../../mvp-survey/mvp-survey.types";
import {
    ErrorBanner,
    FieldWrapper,
    FormWrapper,
    GlobalFormWrapper,
    ClientSpecificButtonsWrapper,
    SingleColumnsWrapper,
    StyledLoader,
    StyledPrimaryButton,
    StyledSecondaryButton,
    SurveyTitle,
    TwoColumnsWrapper,
    TitleAndSurveyWrapper,
} from "../../survey.styles";
import type { FormInputs, Question, QuestionOption } from "../../survey.types";
import { handleSurveySubmit } from "../../utils";

import { BabcockUserType, babcockUserTypeRadioOptions } from "./babcock-survey.types";
import type { BabcockSurveyProps } from "./babcock-survey.types";

const BabcockSurvey = ({
    handleOnContinue,
    clientSurveyData,
    setIsMVP,
    setIsClientSpecific,
    mvpSurveyValues,
}: BabcockSurveyProps) => {
    const intl = useIntl();

    const [isLoading, setIsLoading] = useState(false);
    const [userType, setUserType] = useState<string | undefined>(undefined);
    const [userTypeError, setUserTypeError] = useState<boolean>(false);
    const [hasScrollableArea, setHasScrollableArea] = useState(false);

    const {
        register,
        handleSubmit,
        getValues,
        formState: { errors, isValid },
        control,
        setValue,
        clearErrors,
        reset,
    } = useForm<FormInputs>({
        shouldUseNativeValidation: false,
    });

    const questions: Question[] = clientSurveyData.sections?.[0].questions || [];

    const errorsLength = Object.keys(errors).length;
    const formHasErrors = errorsLength > 0;

    const mappedMvpSurveyValues: MVPFormInputs = mvpSurveyValues.dateOfBirth
        ? {
              ...mvpSurveyValues,
              dateOfBirth: moment(mvpSurveyValues.dateOfBirth, "DD/MM/YYYY"),
          }
        : { ...mvpSurveyValues };

    const onSubmit = async () => {
        if (!userType) {
            setUserTypeError(true);

            return;
        }

        if (userType === BabcockUserType.GLV) {
            //submit only MVP values
            setIsLoading(true);
            await completeRegistration(mappedMvpSurveyValues);
            handleOnContinue();
        } else {
            //submit both MVP & BABCOCK survey values
            await handleSurveySubmit({
                getValues,
                mappedMvpSurveyValues,
                clientSurveyData,
                questions,
                setIsLoading,
                handleOnContinue,
                isValid,
            });
        }
    };

    const onClickGoBack = () => {
        setIsMVP(true);
        setIsClientSpecific(false);
    };

    /* This useEffect checks if content to render is bigger or not than the parent container
    setting hasScrollableArea variable as true or false */
    useEffect(() => {
        const globalFormWrapper = document.getElementById("GlobalFormWrapper");
        const containerMaxHeightWithNoErrors = 39.6875 * 16; //convert to px, because of checkScrollable function
        const containerMaxHeightWithErrors = 41.1875 * 16; //convert to px, because of checkScrollable function

        if (globalFormWrapper) {
            const maxHeight = formHasErrors
                ? containerMaxHeightWithErrors
                : containerMaxHeightWithNoErrors;
            setHasScrollableArea(checkIfScrollable(globalFormWrapper, maxHeight));
        }
    }, [formHasErrors, userType]);

    // Reset form state when simpleRadioValue changes
    useEffect(() => {
        reset();
    }, [userType, reset]);

    // Pre-fill the MVP survey, in case of clicking GoBack button
    useEffect(() => {
        if (mvpSurveyValues) {
            (Object.keys(mvpSurveyValues) as Array<keyof MVPFormInputs>).forEach((key) => {
                const value = mvpSurveyValues[key];

                if (value !== undefined) {
                    setValue(key, value as any);
                }
            });
        }
    }, [mvpSurveyValues, setValue]);

    const simpleRadioQuestionMarginBottom =
        userType === undefined || userType === BabcockUserType.GLV ? "0" : "1.5rem";

    const renderBabcockQuestionnaire = (): ReactNode => {
        return (
            <>
                <TwoColumnsWrapper>
                    <FieldWrapper>
                        {/* index 00 SELECT */}
                        <Select
                            registeredField={questions[0].code}
                            label={questions[0].title}
                            labelMarginBottom={"0.625rem"}
                            labelHeight={"3.375rem"}
                            selectPlaceholder={questions[0].placeHolder}
                            options={questions[0].options as QuestionOption[]}
                            formattedRequiredMessage="survey:required"
                            fieldError={errors?.[questions[0].code]}
                            register={register}
                            setValue={setValue}
                            clearErrors={clearErrors}
                        />
                    </FieldWrapper>

                    <FieldWrapper>
                        {/* index 01 TEXT INPUT */}
                        <TextInput
                            registeredField={questions[1].code}
                            label={questions[1].title}
                            labelMarginBottom={"0.625rem"}
                            labelHeight={"3.375rem"}
                            placeholder={questions[1].placeHolder}
                            formattedRequiredMessage="survey:required"
                            emptyFieldError={errors?.[questions[1].code]}
                            register={register}
                        />
                    </FieldWrapper>

                    <FieldWrapper>
                        {/* index 02 RADIO without input */}
                        <RadioControllerInput<FormInputs>
                            registeredField={questions[2].code}
                            register={register}
                            control={control}
                            errors={errors}
                            label={questions[2].title}
                            labelMarginBottom={"1.5rem"}
                            labelHeight={"3.375rem"}
                            options={questions[2].options as QuestionOption[]}
                            emptyFieldError={errors?.[questions[2].code]} //the global question CODE -> if no radio is selected
                            requiredRule={intl.formatMessage({ id: "survey:required" })}
                        />
                    </FieldWrapper>

                    <FieldWrapper>
                        {/* index 03 RADIO without input */}
                        <RadioControllerInput<FormInputs>
                            registeredField={questions[3].code}
                            register={register}
                            control={control}
                            errors={errors}
                            label={questions[3].title}
                            labelMarginBottom={"1.5rem"}
                            labelHeight={"3.375rem"}
                            options={questions[3].options as QuestionOption[]}
                            emptyFieldError={errors?.[questions[3].code]} //the global question CODE -> if no radio is selected
                            requiredRule={intl.formatMessage({ id: "survey:required" })}
                        />
                    </FieldWrapper>
                </TwoColumnsWrapper>

                <SingleColumnsWrapper marginTop={"1.5rem"}>
                    <FieldWrapper>
                        {/* index 04 SLIDER */}
                        <SurveySlider
                            registeredField={questions[4].code}
                            control={control}
                            options={(questions[4].options ?? []).map((option: QuestionOption) => ({
                                value: parseInt(option.value),
                                label: option.value,
                            }))}
                            step={1}
                            min={1}
                            max={10}
                            label={questions[4].title}
                            labelMarginBottom={"1.5rem"}
                        />
                    </FieldWrapper>
                </SingleColumnsWrapper>
            </>
        );
    };

    return (
        <FormWrapper onSubmit={handleSubmit(onSubmit)}>
            {(Object.keys(errors).length > 0 || userTypeError) && (
                <ErrorBanner>
                    <FormattedMessage id={"survey:error-banner"} />
                </ErrorBanner>
            )}

            <TitleAndSurveyWrapper>
                <SurveyTitle>
                    <FormattedMessage id={"babcock-survey:title"} />
                </SurveyTitle>

                <GlobalFormWrapper
                    id="GlobalFormWrapper"
                    hasScrollableArea={hasScrollableArea}
                    formHasErrors={formHasErrors}
                    maxHeightWithNoErrors={"39.6875rem"}
                    maxHeightWithErrors={"35.4375rem"}
                >
                    <SingleColumnsWrapper marginBottom={simpleRadioQuestionMarginBottom}>
                        <FieldWrapper>
                            {/* index 02 RADIO without input & verification */}
                            <RadioControllerInput<FormInputs>
                                labelFormattedMessage="babcock-survey:simple-radio-question-title"
                                labelMarginBottom={"1.5rem"}
                                options={babcockUserTypeRadioOptions}
                                simpleRadioValue={userType}
                                setSimpleRadioValue={setUserType}
                                clearErrors={clearErrors}
                                clearErrorsTriggerValue={BabcockUserType.FRS}
                                userTypeError={userTypeError}
                                setUserTypeError={setUserTypeError}
                            />
                        </FieldWrapper>
                    </SingleColumnsWrapper>

                    {userType === BabcockUserType.FRS && renderBabcockQuestionnaire()}
                </GlobalFormWrapper>
            </TitleAndSurveyWrapper>

            <ClientSpecificButtonsWrapper hasScrollableArea={hasScrollableArea}>
                <StyledSecondaryButton onClick={onClickGoBack}>
                    <FormattedMessage id={"survey:go-back"} />
                </StyledSecondaryButton>
                <StyledPrimaryButton disabled={isLoading} type="submit">
                    {isLoading ? (
                        <StyledLoader size="medium" color="#666666" fontSize="1.5rem" />
                    ) : (
                        <FormattedMessage id={"survey:continue"} />
                    )}
                </StyledPrimaryButton>
            </ClientSpecificButtonsWrapper>
        </FormWrapper>
    );
};

export default BabcockSurvey;
